import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "./tailwind/dialog";
import { useState } from "react";
import { Button } from "./tailwind/button";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { message } from "antd";

function FloatingProgress({ isVisible, progress, fileName, fileType }) {
  if (!isVisible) return null;

  return (
    <div className="fixed top-[100px] left-[15rem] z-[999999999] bg-white border border-gray-200 rounded-lg shadow-lg p-4 min-w-[300px]">
      <div className="flex items-center space-x-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            Compressing {fileName}
          </p>
          {fileType === "video/mp4" && (
            <>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">{progress}%</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function DialogConvertCompressFile({
  isConvertCompressDialogOpen,
  setIsConvertCompressDialogOpen,
  file,
  setFile,
  setShouldUpload,
  setIsPanelOpened,
  isCompressing,
  setIsCompressing,
  setSelectedNodeConvertCompress,
  setCarouselCardIndex,
  setCarouselComponentIndex,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  if (!file) {
    return;
  }
  const MAX_SIZE_IMAGE = 5 * 1024 * 1024;

  async function convertAndCompressImage() {
    if (!file) {
      return;
    }
    setIsLoading(true);

    setIsConvertCompressDialogOpen(false);
    setIsCompressing(true);
    setCompressionProgress(0);

    let img, canvas, blob, quality;
    let fileToProcess = file;

    if (
      file.type !== "image/jpeg" &&
      file.type !== "application/pdf" &&
      file.type !== "video/mp4"
    ) {
      console.log("conversion");
      img = document.createElement("img");
      img.src = URL.createObjectURL(file);
      await new Promise((res) => (img.onload = res));
      canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      quality = 0.92;
      blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", quality),
      );
      fileToProcess = new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
        type: "image/jpeg",
      });
    }

    if (file.type === "application/pdf") {
      console.log("conversion pdf");
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.js`;

      const arrayBuffer = await file.arrayBuffer();

      const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      console.log(pdfDoc);

      if (pdfDoc.numPages === 0) {
        throw new Error("PDF has no pages");
      }

      const page = await pdfDoc.getPage(1);

      const viewport = page.getViewport({ scale: 1.0 });
      const { width, height } = viewport;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      const maxWidth = 1200;
      const maxHeight = 1600;
      let scale = 1.0;

      if (width > maxWidth || height > maxHeight) {
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        scale = Math.min(widthRatio, heightRatio);
      }

      const scaledViewport = page.getViewport({ scale });
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      await page.render(renderContext).promise;

      quality = 0.92;
      blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", quality),
      );
      fileToProcess = new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
        type: "image/jpeg",
      });
    }

    if (fileToProcess.size > MAX_SIZE_IMAGE) {
      console.log("compress");
      img = document.createElement("img");
      img.src = URL.createObjectURL(fileToProcess);
      await new Promise((res) => (img.onload = res));
      canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      quality = 0.92;
      blob = await new Promise((res) =>
        canvas.toBlob(res, "image/jpeg", quality),
      );
      while (blob.size > MAX_SIZE_IMAGE && quality > 0.1) {
        quality -= 0.05;
        blob = await new Promise((res) =>
          canvas.toBlob(res, "image/jpeg", quality),
        );
      }
      if (blob.size > MAX_SIZE_IMAGE) {
        message.error(
          "The file is too large to be compressed below 5 MB. Please try a smaller file.",
        );
        setIsLoading(false);
        setIsCompressing(false);
        setCompressionProgress(0);
        setIsPanelOpened(true);
        setIsConvertCompressDialogOpen(false);
        return;
      }
      fileToProcess = new File([blob], fileToProcess.name, {
        type: "image/jpeg",
      });
    }

    console.log(`[Process Complete] Setting final file:`, fileToProcess);
    setFile(fileToProcess);
    setShouldUpload(true);
    setIsLoading(false);
    setIsCompressing(false);
    setCompressionProgress(0);
    setIsConvertCompressDialogOpen(false);

    message.success("File compressed successfully!");
  }

  const fileState = isCompressing
    ? "compressing"
    : file?.type === "video/mp4"
      ? "compressVideo"
      : ["image/jpeg", "image/png"].includes(file?.type) &&
          file?.size > MAX_SIZE_IMAGE
        ? "compress"
        : !["image/jpeg", "image/png"].includes(file?.type) &&
            file?.size < MAX_SIZE_IMAGE
          ? "convert"
          : "convertAndCompress";

  let dialogTitle;
  let dialogBody;
  let buttonText;
  switch (fileState) {
    case "compressing":
      dialogTitle = "Compressing your file";
      dialogBody =
        "Please wait for the compression to finish before loading a new file.";
      break;
    case "compressVideo":
      dialogTitle = "Compress your file";
      dialogBody =
        "Your file is larger than 16 MB. Videos under 1 minute can be compressed. Would you like to compress it?";
      buttonText = "Compress";
      break;
    case "compress":
      dialogTitle = "Compress your file";
      dialogBody =
        "Your file is larger than 5 MB. Would you like to compress it?";
      buttonText = "Compress";
      break;
    case "convert":
      dialogTitle = "Convert your file";
      dialogBody =
        "Your file format is not supported for WhatsApp messages. Would you like to convert it to a supported format?";
      buttonText = "Convert";
      break;
    case "convertAndCompress":
    default:
      dialogTitle = "Convert and compress your file";
      dialogBody =
        "Your file is too large to send and its format is not supported for WhatsApp messages. Would you like to convert and compress it?";
      buttonText = "Convert and Compress";
      break;
  }

  return (
    <>
      <FloatingProgress
        isVisible={isCompressing}
        progress={compressionProgress}
        fileName={file?.name || "file"}
        fileType={file?.type || "file"}
      />
      <Dialog
        key="dialog_archive"
        open={isConvertCompressDialogOpen}
        onClose={() => {
          setIsPanelOpened(true);
          setIsConvertCompressDialogOpen(false);
          setSelectedNodeConvertCompress(null);
          setCarouselCardIndex?.(null);
          setCarouselComponentIndex?.(null);
        }}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        {dialogBody}
        {!isCompressing && (
          <div className="flex mt-2">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-700 mr-1" />
            <p className="text-xs">
              This may reduce image quality. Please verify the result before
              sending the campaign.
            </p>
          </div>
        )}
        <DialogBody className="flex-1 overflow-y-auto scrollbar"></DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => {
              setIsPanelOpened(true);
              setIsConvertCompressDialogOpen(false);
              if (!isCompressing) {
                setSelectedNodeConvertCompress(null);
                setCarouselCardIndex?.(null);
                setCarouselComponentIndex?.(null);
              }
            }}
            disabled={isLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          {!isCompressing && (
            <Button
              color="primaryButton"
              onClick={() => {
                convertAndCompressImage();
              }}
              className="min-w-[100px] h-10"
              loading={isLoading}
              disabled={isCompressing}
            >
              {buttonText}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
